import type { ReplayFrameEvent } from '@sentry/react';

export const SET_PASSWORD_PATH = '/set-password';
export const PASSWORD_SETUP_TOKEN_STORAGE_KEY = 'sr-password-setup-token';

// Must run before GTM and Sentry boot so they never read the token. Browser
// history has already saved the opened link by then; no script can undo that.
export function captureSetupTokenFromUrl(win: Window = window): void {
  if (win.location.pathname !== SET_PASSWORD_PATH) return;

  const token = new URLSearchParams(win.location.hash.slice(1)).get('token');

  if (!token) return;

  try {
    win.sessionStorage.setItem(PASSWORD_SETUP_TOKEN_STORAGE_KEY, token);
  } catch {
    // Storage blocked: the page falls back to the "email me a link" form.
  }

  win.history.replaceState(
    win.history.state,
    '',
    `${win.location.pathname}${win.location.search}`,
  );
}

// The navigation timing entry keeps the loaded URL, token included, after
// replaceState, and Session Replay uploads it as a span description.
export function scrubSetupTokenFromReplayEvent(
  event: ReplayFrameEvent,
): ReplayFrameEvent {
  if (event.data.tag === 'performanceSpan') {
    event.data.payload.description =
      event.data.payload.description.split('#')[0];
  }

  return event;
}

export function readCapturedSetupToken(): null | string {
  try {
    return sessionStorage.getItem(PASSWORD_SETUP_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearCapturedSetupToken(): void {
  try {
    sessionStorage.removeItem(PASSWORD_SETUP_TOKEN_STORAGE_KEY);
  } catch {
    // Nothing stored, nothing to clear.
  }
}

// Display only (the server checks the signature): lets an expired link offer
// a fresh one without the member typing their email.
export function emailFromSetupToken(token: string): null | string {
  const payload = token.split('.')[1];

  if (!payload) return null;

  try {
    const binary = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const json = JSON.parse(
      new TextDecoder().decode(Uint8Array.from(binary, (c) => c.charCodeAt(0))),
    );

    return typeof json.sub === 'string' ? json.sub : null;
  } catch {
    return null;
  }
}
